import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import type { AuthState } from '@workflow/contracts'
import { API_BASE } from '@workflow/contracts'
import { Button, Field, SHELL, TextField, WorkflowMark } from '@workflow/ui'
import { api, ApiError, describeError } from '@/lib/api/client'
import { keys } from '@/lib/api/keys'
import { useDevSignIn } from '@/lib/api/hooks'
import { navigate } from '@/app/router'
import { useCookieChoice } from './cookies'
import { CookieBanner } from './CookieBanner'
import { startOneTap, type OneTapHandle } from './oneTap'

/** The official Google "G", drawn on its own 48-grid. The one multicolor mark
 * in the app that is not ours; it lives inside the button and nowhere else. */
function GoogleMark({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

/**
 * The door, full-frame in two halves.
 *
 * The left half is the studio's painting edge to edge, with the wordmark
 * standing on it the way a gallery labels its wall. The right half is flat
 * ground — no card inside a card — carrying the voice and the one commit:
 * continue with Google, the page's own ink on the page itself. Membership is
 * closed, so a quiet line under the button says the address must already be
 * invited. Phones stack the painting above the words.
 */
/** The refusal word the server put inside the error's details, if it did —
 * the same vocabulary the OAuth callback sends home in the URL. */
function refusalOf(error: unknown): { authError: string; email: string | null } | null {
  if (!(error instanceof ApiError)) return null
  const details = error.details as { authError?: unknown; email?: unknown } | null
  if (!details || typeof details.authError !== 'string') return null
  return { authError: details.authError, email: typeof details.email === 'string' ? details.email : null }
}

export function SignInScreen({ auth }: { auth: AuthState['auth'] }) {
  const [devOpen, setDevOpen] = useState(false)
  const [unconfigured, setUnconfigured] = useState(false)
  const queryClient = useQueryClient()

  // One Tap, laid over the door — and only with permission. Google's floating
  // "continue as …" prompt is the optional layer, so it waits for Accept all;
  // essentials-only and the unanswered stay without it. Desktops only: phones
  // and tablets never see an attempt — the button below is their door — and
  // where the prompt is skipped or dismissed nothing changes at all.
  const consent = useCookieChoice()
  useEffect(() => {
    const clientId = auth.googleClientId
    if (!auth.google || !clientId || devOpen || consent?.optional !== true) return
    let handle: OneTapHandle | null = null
    let torn = false
    const timer = window.setTimeout(() => {
      void startOneTap({
        clientId,
        onCredential: (credential) => {
          api
            .oneTap(credential)
            .then(() => queryClient.invalidateQueries({ queryKey: keys.session }))
            .catch((error: unknown) => {
              const refusal = refusalOf(error)
              if (!refusal) return
              const target = new URL('/', window.location.origin)
              target.searchParams.set('authError', refusal.authError)
              if (refusal.email) target.searchParams.set('email', refusal.email)
              window.location.href = target.toString()
            })
        },
      }).then((started) => {
        handle = started
        if (torn) handle?.cancel()
      })
    }, 1000)
    return () => {
      torn = true
      window.clearTimeout(timer)
      handle?.cancel()
    }
  }, [auth.google, auth.googleClientId, devOpen, consent, queryClient])

  /** With credentials, straight to Google's page. Without them, one step down
   * to the development door — same button, because the door looks the same.
   * A server with neither (deployed before its OAuth secrets arrive) says so
   * rather than opening a door that does not exist. */
  const continueWithGoogle = () => {
    if (auth.google) window.location.href = `${API_BASE}/auth/oauth/start`
    else if (auth.devSignIn) setDevOpen(true)
    else setUnconfigured(true)
  }

  return (
    <div className="flex min-h-dvh select-none flex-col bg-surface-sunken sm:flex-row">
      {/* The painting, edge to edge, with the wordmark standing on it. */}
      <div className="relative min-h-72 overflow-hidden sm:min-h-dvh sm:w-1/2">
        <img
          src="/art/pastel-sea.jpg"
          alt="A watercolour path over a pastel sea"
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-transparent" aria-hidden />
        <header className="absolute top-0 right-0 left-0 flex items-center gap-2 px-6 text-white" style={{ height: SHELL.topStripHeight }}>
          <WorkflowMark size={18} className="text-white" />
          <span className="wf-heading text-white">Workflow</span>
        </header>
      </div>

      {/* Flat ground: the voice and the commit, the page's own ink. */}
      <div className="relative flex flex-1 items-center px-8 py-14 sm:px-14 lg:px-20">
        <div className="w-full max-w-md">
          <p className="text-[12px] font-medium uppercase tracking-[0.34em] text-muted-foreground">
            Workflow X TLS Studio
          </p>
          <p className="mt-6 max-w-[12em] text-balance font-display text-[clamp(2.25rem,4vw,3.25rem)] leading-[1.06] text-foreground">
            The work <span className="italic">behind</span> the work.
          </p>
          <p className="mt-4 max-w-[30em] text-[15px] leading-relaxed text-muted-foreground">
            The studio's console in one place.
          </p>

          <div className="mt-9">
            {/* The commit, with a spring in it: lifting on approach, giving
                under the press, settling back — a handshake, not a blink. */}
            <motion.button
              type="button"
              onClick={continueWithGoogle}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.94 }}
              transition={{ type: 'spring', stiffness: 550, damping: 26 }}
              className="flex h-[52px] cursor-pointer items-center gap-3 rounded-full bg-foreground px-8 text-[15px] font-medium text-surface shadow-[0_16px_36px_-18px_rgba(15,15,22,0.55)] outline-none hover:shadow-[0_22px_44px_-18px_rgba(15,15,22,0.6)] focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-surface-sunken"
            >
              <GoogleMark />
              Continue with Google
            </motion.button>
            <p className="mt-4 max-w-[30em] text-[12.5px] leading-relaxed text-muted-foreground">
              Membership is by invitation — the address must already be on the list.
            </p>
            {unconfigured && !auth.google && !auth.devSignIn ? (
              <p className="mt-3 max-w-[30em] text-[12.5px] leading-relaxed text-muted-foreground">
                Sign-in with Google is not configured on this server yet.
                The administrator adds the OAuth credentials, and this door opens.
              </p>
            ) : null}
          </div>
        </div>

        {/* The footnote: the year the clock says, and the three internal
            pages. Small on purpose — a note, not a nav. */}
        <DoorFootnote />
      </div>

      <CookieBanner />
      {devOpen && !auth.google ? <DevSignIn onDone={() => setDevOpen(false)} /> : null}
    </div>
  )
}

/** © the year the clock says — never hardcoded — with privacy, terms, cookies. */
function DoorFootnote() {
  const year = new Date().getFullYear()
  const link =
    'px-1 py-1 text-caption text-muted-foreground underline-offset-4 transition-colors duration-fast outline-none hover:text-foreground hover:underline focus-visible:ring-1 focus-visible:ring-foreground'
  return (
    <footer className="absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-x-1 gap-y-1 px-8 pb-5 sm:px-14 lg:px-20">
      <span className="py-1 text-caption text-muted-foreground">© {year} Workflow X TLS Studio</span>
      <span aria-hidden className="text-caption text-muted-foreground/50">·</span>
      <button type="button" onClick={() => navigate({ name: 'privacy' })} className={link}>Privacy</button>
      <button type="button" onClick={() => navigate({ name: 'terms' })} className={link}>Terms</button>
      <button type="button" onClick={() => navigate({ name: 'cookies' })} className={link}>Cookies</button>
    </footer>
  )
}

/**
 * The development door, one click below the button. It exists only while
 * Google OAuth is unconfigured and the API runs in development — the moment
 * credentials exist, this sheet is unreachable. It keeps the local flow
 * exercisable without putting a form on the face of the door.
 *
 * It is an overlay, so it behaves like one: Escape closes it, and focus
 * returns to whoever opened it.
 */
function DevSignIn({ onDone }: { onDone: () => void }) {
  const devSignIn = useDevSignIn()
  const [email, setEmail] = useState('')
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onDone()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [onDone])

  return (
    <div className="wf-blur fixed inset-0 z-40 flex items-center justify-center p-4">
      <div ref={panelRef} className="w-full max-w-[22rem] rounded-xl border bg-surface p-6 shadow-panel">
        <h2 className="wf-heading text-foreground">Development Sign-in</h2>
        <p className="mt-1.5 text-caption text-muted-foreground">
          Google OAuth is not configured on this server yet. Membership is by invitation: the
          address must already be invited.
        </p>
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            devSignIn.mutate(email)
          }}
        >
          <Field label="Work email" htmlFor="dev-signin-email">
            <TextField
              id="dev-signin-email"
              value={email}
              placeholder="you@company.com"
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <div className="flex items-center gap-2">
            <Button type="submit" variant="primary" size="sm" busy={devSignIn.isPending} disabled={!email.trim()}>
              Sign in
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onDone}>
              Cancel
            </Button>
          </div>
          {devSignIn.error ? <p className="text-caption text-danger">{describeError(devSignIn.error)}</p> : null}
        </form>
      </div>
    </div>
  )
}
